import { CompetitionType } from "app/types/competitionType";
import { EventStatus } from "app/utils/const";
import cx from "classnames";
import { useEffect, useState, type Node } from "react";
import { connect } from "react-redux";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import ShowMoreLessButton from "./components/ShowMoreLessButton/ShowMoreLessButton";
import "./PopularEventsCollapsible.scss";

type Props = {
  status?: string;
  suspended?: Node;
  header: Node;
  content: Node;
  onToggle?: (...args: Array<any>) => any;
  isActive: boolean;
  withArrows?: boolean;
  disableAnimations?: boolean;
  data: CompetitionType;
  addExpandedCompetitionNavigationNodeId: Function;
  removeExpandedCompetitionNavigationNodeId: Function;
  resetExpandedCompetitions: Function;
  setCompetitions: Function;
  setCompetitionListLoading: boolean;
  expandedCompetitionEvents: Array<number>;
};

const PopularEventsCollapsible: React.FC<Props> = props => {
  const [showMoreEvents, setShowMoreEvents] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [componentIsVisibleAgain, setComponentIsVisibleAgain] = useState(false);
  const [hasFiveOrMoreEvents, setHasFiveOrMoreEvents] = useState(false);

  //toggle showMoreEvents state
  const handleShowMoreEvents = () => {
    setShowMoreEvents(!showMoreEvents);
  };

  useEffect(() => {
    setMounted(true);
    // check if component has been previously mounted and if competition has been set to expanded
    if (props.expandedCompetitionEvents.includes(props.data.navigationNodeId)) {
      setComponentIsVisibleAgain(true);
      setShowMoreEvents(true);
    }
    // check if competition has 5 or more events if so display show more button
    if (props.data.nbOfEvent > 5) {
      setHasFiveOrMoreEvents(true);
    }
  }, []);

  // check if competition has 6 or more events if so display show more button
  useEffect(() => {
    setHasFiveOrMoreEvents(props.data.nbOfEvent > 5);
  }, [props.data.events]);

  useEffect(() => {
    if (mounted && showMoreEvents && !componentIsVisibleAgain) {
      // add competition navigationNodeId to expanded competitions list in redux store
      props.addExpandedCompetitionNavigationNodeId(props.data.navigationNodeId);
      props.setCompetitions();
      setShowLoading(true);
    } else if (mounted && !showMoreEvents) {
      // remove competition navigationNodeId from expanded competitions list in redux store
      props.removeExpandedCompetitionNavigationNodeId(
        props.data.navigationNodeId
      );
      props.setCompetitions();
      setShowLoading(true);
    } else if (componentIsVisibleAgain) {
      // step necessary if component is visible again after being removed as a result of scrolling
      setComponentIsVisibleAgain(false);
    }
  }, [showMoreEvents]);

  useEffect(() => {
    if (!props.setCompetitionListLoading && showLoading) {
      setShowLoading(false);
    }
  }, [props.setCompetitionListLoading]);

  const {
    status,
    suspended,
    header,
    content,
    onToggle,
    withArrows,
    disableAnimations,
  } = props;

  const isActive = props.isActive ?? true;

  const showOverlay =
    status === EventStatus.SUSPENDED.toLowerCase() ||
    status === EventStatus.GRADED.toLowerCase() ||
    status === EventStatus.CLOSED.toLowerCase();
  let statusCssClass = "";

  if (status != null) {
    switch (status) {
      case EventStatus.SUSPENDED.toLowerCase():
        statusCssClass = EventStatus.SUSPENDED.toLowerCase();
        break;

      case EventStatus.GRADED.toLowerCase():
        statusCssClass = EventStatus.SUSPENDED.toLowerCase();
        break;

      case EventStatus.CLOSED.toLowerCase():
        statusCssClass = EventStatus.SUSPENDED.toLowerCase();
        break;

      default:
        statusCssClass = status;
        break;
    }
  } else {
    statusCssClass = "";
  }

  return (
    <div
      className={cx(`popular-events-collapsible ${statusCssClass} suspended`, {
        "popular-events-collapsible--active": isActive,
      })}
    >
      {showOverlay ? suspended : ""}
      <div
        className={cx("collapsible__header", {
          "collapsible__header--with-arrows": withArrows,
          "collapsible__header--with-arrows--up": withArrows && isActive,
          "collapsible__header--with-arrows--down": withArrows && !isActive,
        })}
        onClick={onToggle}
      >
        {header}
      </div>
      <div
        className={`popular-events-collapsible__content ${
          showLoading ? "disabled" : ""
        }`}
      >
        {showLoading ? <LoadingSpinner /> : ""}
        <div
          className={cx("popular-events-collapsible__content__inner", {
            "popular-events-collapsible__content__inner--disable-animations":
              disableAnimations,
          })}
        >
          {content}
        </div>
      </div>
      {hasFiveOrMoreEvents && (
        <ShowMoreLessButton
          navigationNodeId={props.data.navigationNodeId}
          showMoreEvents={showMoreEvents}
          showLoading={showLoading}
          handleShowMoreEvents={handleShowMoreEvents}
        />
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  disableFilterTab: state.competitionListModel.disableFilterTab,
  type: state.competitionsListFiltersModel.type,
  setCompetitionListLoading:
    state.competitionListModel.setCompetitionListLoading,
  expandedCompetitionEvents:
    state.competitionsListFiltersModel.expandedCompetitionEvents,
});

function mapDispatchToProps(dispatch) {
  return {
    addExpandedCompetitionNavigationNodeId: (navigationNodeId: number) =>
      dispatch.competitionsListFiltersModel.addExpandedCompetitionNavigationNodeId(
        navigationNodeId
      ),
    removeExpandedCompetitionNavigationNodeId: (navigationNodeId: number) =>
      dispatch.competitionsListFiltersModel.removeExpandedCompetitionNavigationNodeId(
        navigationNodeId
      ),
    resetExpandedCompetitions: () =>
      dispatch.competitionsListFiltersModel.resetExpandedCompetitions(),
    setCompetitions: options => {
      dispatch.competitionListModel.setEuroCompetitionList(options);
    },
  };
}
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PopularEventsCollapsible);
